/**
 * Date utilities for consistent timezone handling.
 *
 * The backend stores all timestamps in UTC. These utilities ensure
 * dates are properly converted to the user's local timezone for display.
 */

/**
 * Formats a date string from the API (UTC) to local time.
 * @param dateString - ISO date string from API (e.g., "2025-12-26T04:38:15.327412+01:00")
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string in user's local timezone
 */
export function formatDate(
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }
): string {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';

    return new Intl.DateTimeFormat(undefined, options).format(date);
  } catch {
    return 'Invalid date';
  }
}

/**
 * Formats a date with time in user's local timezone.
 * @param dateString - ISO date string from API
 * @returns Formatted date and time string (e.g., "Dec 26, 2025, 5:38 AM")
 */
export function formatDateTime(dateString: string | null | undefined): string {
  return formatDate(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Formats a date with full time including seconds.
 * @param dateString - ISO date string from API
 * @returns Formatted date and time string (e.g., "Dec 26, 2025, 5:38:15 AM")
 */
export function formatDateTimeFull(dateString: string | null | undefined): string {
  return formatDate(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Formats time only in user's local timezone.
 * @param dateString - ISO date string from API
 * @returns Formatted time string (e.g., "5:38 AM")
 */
export function formatTime(dateString: string | null | undefined): string {
  return formatDate(dateString, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Formats a date relative to now (e.g., "2 hours ago", "yesterday").
 * @param dateString - ISO date string from API
 * @returns Relative time string
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    // Fall back to regular date format for older dates
    return formatDate(dateString);
  } catch {
    return 'Invalid date';
  }
}

/**
 * Gets the user's timezone name.
 * @returns Timezone string (e.g., "Europe/Belgrade", "America/New_York")
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Converts a local date input to UTC ISO string for sending to the API.
 * @param localDate - Date object or string from local input
 * @returns ISO string in UTC
 */
export function toUTCString(localDate: Date | string | null | undefined): string | null {
  if (!localDate) return null;

  try {
    const date = typeof localDate === 'string' ? new Date(localDate) : localDate;
    if (isNaN(date.getTime())) return null;

    return date.toISOString();
  } catch {
    return null;
  }
}
